// Pagination.jsx
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Pagination = ({ paginate, setFilter, setTablePage, type }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const getCurrentPageForTable = useCallback(() => {
    if (type) {
      const pageKey = `${type}_page`;
      const pageFromURL = searchParams.get(pageKey);
      return pageFromURL ? parseInt(pageFromURL) : 1;
    }
    const pageFromURL = searchParams.get("page");
    return pageFromURL ? parseInt(pageFromURL) : 1;
  }, [searchParams, type]);

  const { totalPages = 1, pageNumber = 1, hasNext: hasNextFromApi, perPage } =
    paginate || {};

  const currentPage = pageNumber || getCurrentPageForTable();
  const hasPrevious = currentPage > 1;
  const hasNext =
    typeof hasNextFromApi === "boolean"
      ? hasNextFromApi
      : currentPage < totalPages;

  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage < 1 || newPage > totalPages) return;

      if (setTablePage && type) {
        setTablePage(type, newPage);
      } else if (setFilter) {
        if (type) {
          setFilter({ [`${type}_page`]: newPage });
        } else {
          setFilter({ page: newPage });
        }
      }
    },
    [setFilter, setTablePage, totalPages, type]
  );

  const getPageNumbers = useCallback(() => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      if (currentPage <= 3) {
        endPage = maxVisiblePages;
      } else if (currentPage >= totalPages - 2) {
        startPage = totalPages - maxVisiblePages + 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  }, [currentPage, totalPages]);

  const pageNumbers = getPageNumbers();

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="flex items-center justify-center gap-3 py-4">
      <button
        disabled={!hasPrevious}
        onClick={() => handlePageChange(currentPage - 1)}
        className={`p-2 rounded-lg transition-colors ${
          !hasPrevious
            ? "opacity-30 cursor-not-allowed bg-gray-200"
            : "cursor-pointer hover:bg-gray-100 bg-white border border-gray-300"
        }`}
        aria-label={t("common.previous")}
      >
        <img
          src="/img/arrow.svg"
          alt={t("common.previous")}
          className="rotate-180 rtl:rotate-0"
        />
      </button>

      <div className="flex gap-1">
        {pageNumbers[0] > 1 && (
          <>
            <button
              onClick={() => handlePageChange(1)}
              className={`font-medium w-8 h-8 rounded-lg flex items-center justify-center ${
                1 === currentPage
                  ? "bg-primary text-white"
                  : "hover:bg-gray-100 text-gray-700 border border-gray-300"
              }`}
            >
              1
            </button>
            {pageNumbers[0] > 2 && (
              <span className="flex items-center px-1 text-gray-500">...</span>
            )}
          </>
        )}

        {pageNumbers.map((page) => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`font-medium w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
              page === currentPage
                ? "bg-primary text-white border border-primary"
                : "hover:bg-gray-100 text-gray-700 border border-gray-300"
            }`}
          >
            {page}
          </button>
        ))}

        {pageNumbers[pageNumbers.length - 1] < totalPages && (
          <>
            {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
              <span className="flex items-center px-1 text-gray-500">...</span>
            )}
            <button
              onClick={() => handlePageChange(totalPages)}
              className={`font-medium w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer ${
                totalPages === currentPage
                  ? "bg-primary text-white"
                  : "hover:bg-gray-100 text-gray-700 border border-gray-300"
              }`}
            >
              {totalPages}
            </button>
          </>
        )}
      </div>

      <button
        disabled={!hasNext}
        onClick={() => handlePageChange(currentPage + 1)}
        className={`p-2 rounded-lg transition-colors ${
          !hasNext
            ? "opacity-30 cursor-not-allowed bg-gray-200"
            : "cursor-pointer hover:bg-gray-100 bg-white border border-gray-300"
        }`}
        aria-label={t("common.next")}
      >
        <img
          src="/img/arrow.svg"
          alt={t("common.next")}
          className="rtl:rotate-180"
        />
      </button>

      <div className="ms-4 text-sm text-gray-600">
        {t("common.page")} {currentPage} {t("common.of")} {totalPages}
        {perPage ? ` · ${perPage} ${t("common.perPage")}` : ""}
      </div>
    </div>
  );
};

export default Pagination;
